import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { EntryPath } from '@/Config/app/router/entry.path';
import { ReCaptchaCheckbox } from '@/Domain/components/Auth/ReCaptchaCheckbox';
import { GoogleButton } from '@/Domain/components/Auth/SocialButton/GoogleButton';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { PasswordInput } from '@/Domain/components/Shared/Input/PasswordInput';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { EnvelopIcon } from '@/Domain/components/icons/Envelop.icon';
import LockLineIcon from '@/Domain/components/icons/LockLineIcon';
import { SocialPlatformEnum } from '@/Domain/enum/SocialPlatform.enum';
import { useLogin } from '@/Domain/hooks/Auth/useLogin.hook';
import { useSocialLogin } from '@/Domain/hooks/Auth/useSocialLogin.hook';

export const LoginPage = () => {
  const { t } = useTranslation('login');
  const { search } = useLocation();

  const {
    useV2,
    control,
    register,
    handleSubmit,
    errors,
    isPending,
    onSubmit,
    onVerify,
    tooManyRequests,
  } = useLogin();

  const { handleSocialLogin } = useSocialLogin();

  return (
    <div className="animate-in fade-in duration-300">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-semibold text-2xl text-gray-950 mb-2">{t('title')}</h1>
        <p className="text-gray-500 text-sm">{t('subtitle')}</p>
      </div>

      <div className="grid gap-5">
        {/* Social Login */}
        <div className="grid gap-4">
          <GoogleButton
            onClick={(event) =>
              handleSocialLogin(event, SocialPlatformEnum.Google, '/login/google-oauth2/')
            }
            disabled={isPending}
          />
        </div>

        {/* Divider */}
        <div className="relative flex pt-1 items-center">
          <div className="flex-grow border-t border-gray-200"></div>
          <span className="flex-shrink mx-4 text-gray-400 text-xs font-medium uppercase tracking-wide">
            {t('or')}
          </span>
          <div className="flex-grow border-t border-gray-200"></div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <GoogleReCaptcha onVerify={onVerify} />

          {/* Email Field */}
          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm font-medium text-gray-700">
              {t('form.email')}
            </label>
            <Input
              id="email"
              size="lg"
              {...register('username')}
              placeholder={t('form.email_placeholder')}
              error={!!errors.username}
              autoFocus
              autoComplete="email"
              startIcon={<EnvelopIcon className="h-4 w-4 text-gray-400" />}
            />
            {errors.username && (
              <p className="text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {errors.username.message}
              </p>
            )}
          </div>

          {/* Password Field */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="text-sm font-medium text-gray-700">
                {t('form.password')}
              </label>
              <Link
                to={EntryPath.forgetPassword}
                className="text-sm text-gray-700 hover:text-black font-medium transition-colors hover-underline"
                tabIndex={-1}
              >
                {t('link.forgot_password')}
              </Link>
            </div>
            <PasswordInput
              id="password"
              size="lg"
              placeholder={t('form.password_placeholder')}
              {...register('password')}
              error={!!errors.password}
              autoComplete="current-password"
              startIcon={<LockLineIcon className="h-4 w-4 text-gray-400" />}
            />
            {errors.password && (
              <p className="text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Too Many Requests Error */}
          {tooManyRequests && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 animate-in fade-in slide-in-from-top-2 duration-200">
              <p className="text-sm text-red-700 font-medium">{t('ERRORS:TOO_MANY_REQUESTS')}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            variant="dark"
            size="lg"
            type="submit"
            disabled={isPending}
            className="w-full mt-1"
          >
            {isPending ? <Spinner /> : t('button.login')}
          </Button>

          {/* reCAPTCHA v2 Fallback */}
          {useV2 && (
            <Controller
              name="recaptcha_response_v2"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange }, fieldState: { invalid } }) => (
                <ReCaptchaCheckbox onChange={onChange} isInvalid={invalid} />
              )}
            />
          )}
        </form>

        {/* Register Link */}
        <div className="text-center pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            {t('link.register_prompt')}{' '}
            <Link
              to={`${EntryPath.register}${search}`}
              className="text-gray-700 hover:text-black font-medium transition-colors hover-underline"
            >
              {t('link.register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
